import React, { useEffect, useMemo, useState } from "react";
import { Button, Spin } from "antd";
import { ArrowRight, Check, Home, ShieldCheck, Sparkles } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchCommercialUsage } from "../../Actions/commercialControl.action";
import "./BillingReturnPage.css";

const ACTIVE_SUBSCRIPTION_STATUSES = new Set(["active", "trialing"]);

const getUsageSnapshot = (commercialControl = {}) => (
    commercialControl.usageSnapshot?.data || commercialControl.usageSnapshot || {}
);

const getPlanFromCommercialControl = (commercialControl = {}) => (
    commercialControl.plan ||
    getUsageSnapshot(commercialControl)?.plan ||
    null
);

const getSubscriptionStatus = (commercialControl = {}, plan = null) => {
    const usageSnapshot = getUsageSnapshot(commercialControl);
    return String(
        plan?.status ||
        plan?.subscription_status ||
        usageSnapshot?.subscription_status ||
        usageSnapshot?.subscription?.status ||
        usageSnapshot?.state?.status ||
        ""
    ).trim().toLowerCase();
};

export const isUsageShowingActivePlan = (commercialControl = {}) => {
    const plan = getPlanFromCommercialControl(commercialControl);
    const status = getSubscriptionStatus(commercialControl, plan);
    return Boolean(plan?.code && ACTIVE_SUBSCRIPTION_STATUSES.has(status));
};

const formatPlanLabel = (plan) => {
    const raw = plan?.name || plan?.code;
    return raw ? String(raw).trim().toUpperCase() : null;
};

const BillingReturnPage = ({ variant = "success" }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation("common");
    const commercialControl = useSelector((state) => state.commercialControl || {});
    const [refreshStatus, setRefreshStatus] = useState(variant === "success" ? "loading" : "idle");
    const isSuccess = variant === "success";
    const copy = useMemo(() => ({
        successKicker: t("billingReturn.successKicker", { defaultValue: "Verdictia Billing" }),
        successTitle: t("billingReturn.successTitle", { defaultValue: "Paiement reçu. Votre accès se met en place." }),
        activeTitle: t("billingReturn.activeTitle", { defaultValue: "Votre espace est prêt." }),
        successBody: t("billingReturn.successBody", {
            defaultValue: "Merci pour votre confiance. Verdictia confirme maintenant les droits depuis le backend afin de garder votre workspace parfaitement aligné avec Stripe.",
        }),
        activeBody: t("billingReturn.activeBody", {
            defaultValue: "Votre paiement est confirmé et votre plan est actif. Vous pouvez reprendre le travail depuis l'accueil.",
        }),
        cancelKicker: t("billingReturn.cancelKicker", { defaultValue: "Paiement interrompu" }),
        cancelTitle: t("billingReturn.cancelTitle", { defaultValue: "Aucun changement n’a été appliqué." }),
        cancelBody: t("billingReturn.cancelBody", {
            defaultValue: "La session de paiement a été annulée. Votre workspace conserve son état actuel.",
        }),
        homeCta: t("billingReturn.homeCta", { defaultValue: "Retour à l’accueil" }),
        verifying: t("billingReturn.verifying", { defaultValue: "Vérification sécurisée de votre plan..." }),
        activePlan: t("billingReturn.activePlan", { defaultValue: "Plan actif" }),
        activeSource: t("billingReturn.activeSource", { defaultValue: "Statut confirmé par le backend" }),
        pendingTitle: t("billingReturn.pendingTitle", { defaultValue: "Confirmation en cours" }),
        pendingBody: t("billingReturn.pendingBody", {
            defaultValue: "Les droits seront mis à jour automatiquement dès que Stripe confirme le paiement.",
        }),
        errorTitle: t("billingReturn.errorTitle", { defaultValue: "Vérification différée" }),
        errorBody: t("billingReturn.errorBody", {
            defaultValue: "Le paiement peut être confirmé par webhook. Revenez à l'accueil et actualisez l'usage dans quelques instants.",
        }),
        stepPayment: t("billingReturn.stepPayment", { defaultValue: "Paiement reçu" }),
        stepBackend: t("billingReturn.stepBackend", { defaultValue: "Synchronisation backend" }),
        stepWorkspace: t("billingReturn.stepWorkspace", { defaultValue: "Workspace prêt" }),
    }), [t]);

    useEffect(() => {
        if (!isSuccess) return undefined;

        let mounted = true;
        const result = dispatch(fetchCommercialUsage());
        Promise.resolve(result)
            .then(() => {
                if (mounted) setRefreshStatus("done");
            })
            .catch(() => {
                if (mounted) setRefreshStatus("error");
            });

        return () => {
            mounted = false;
        };
    }, [dispatch, isSuccess]);

    const planLabel = useMemo(
        () => formatPlanLabel(getPlanFromCommercialControl(commercialControl)),
        [commercialControl],
    );
    const showsActivePlan = isUsageShowingActivePlan(commercialControl);
    const statusTone = refreshStatus === "error" ? "warning" : showsActivePlan ? "active" : "pending";
    const title = showsActivePlan ? copy.activeTitle : copy.successTitle;
    const body = showsActivePlan ? copy.activeBody : copy.successBody;

    if (!isSuccess) {
        return (
            <div className="billing-return-page billing-return-page--cancel">
                <section className="billing-return-hero" aria-labelledby="billing-return-title">
                    <div className="billing-return-hero__mark billing-return-hero__mark--cancel">
                        <Home size={28} strokeWidth={1.9} />
                    </div>
                    <p className="billing-return-hero__kicker">{copy.cancelKicker}</p>
                    <h1 id="billing-return-title">{copy.cancelTitle}</h1>
                    <p className="billing-return-hero__lead">{copy.cancelBody}</p>
                    <Button
                        type="primary"
                        size="large"
                        className="billing-return-hero__button"
                        onClick={() => navigate("/workspace")}
                    >
                        {copy.homeCta}
                        <ArrowRight size={16} strokeWidth={2.4} />
                    </Button>
                </section>
            </div>
        );
    }

    return (
        <div className="billing-return-page">
            <section className="billing-return-hero" aria-labelledby="billing-return-title">
                <div className="billing-return-hero__mark">
                    <Check size={34} strokeWidth={2.4} />
                </div>
                <p className="billing-return-hero__kicker">{copy.successKicker}</p>
                <h1 id="billing-return-title">{title}</h1>
                <p className="billing-return-hero__lead">{body}</p>

                <div className="billing-return-progress" aria-label={copy.successKicker}>
                    <span className="billing-return-progress__item is-complete">
                        <Check size={14} />
                        {copy.stepPayment}
                    </span>
                    <span className={`billing-return-progress__item ${refreshStatus !== "loading" ? "is-complete" : "is-current"}`}>
                        {refreshStatus === "loading" ? <Spin size="small" /> : <ShieldCheck size={14} />}
                        {copy.stepBackend}
                    </span>
                    <span className={`billing-return-progress__item ${showsActivePlan ? "is-complete" : ""}`}>
                        <Sparkles size={14} />
                        {copy.stepWorkspace}
                    </span>
                </div>

                <Button
                    type="primary"
                    size="large"
                    className="billing-return-hero__button"
                    onClick={() => navigate("/workspace")}
                >
                    {copy.homeCta}
                    <ArrowRight size={16} strokeWidth={2.4} />
                </Button>
            </section>

            <section className={`billing-return-status billing-return-status--${statusTone}`}>
                {refreshStatus === "loading" ? (
                    <div className="billing-return-status__loading">
                        <Spin size="small" />
                        <span>{copy.verifying}</span>
                    </div>
                ) : (
                    <>
                        <div className="billing-return-status__icon">
                            {showsActivePlan ? <ShieldCheck size={22} /> : <Sparkles size={21} />}
                        </div>
                        <div>
                            <p className="billing-return-status__label">
                                {showsActivePlan
                                    ? `${copy.activePlan} : ${planLabel}`
                                    : refreshStatus === "error"
                                    ? copy.errorTitle
                                    : copy.pendingTitle}
                            </p>
                            <p className="billing-return-status__text">
                                {showsActivePlan
                                    ? copy.activeSource
                                    : refreshStatus === "error"
                                    ? copy.errorBody
                                    : copy.pendingBody}
                            </p>
                        </div>
                    </>
                )}
            </section>
        </div>
    );
};

export default BillingReturnPage;
